import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { isAdminRole, openOpportunity } from '../../../utils/openOpportunity'
import { useMemo } from 'react'

const ChartContainer = styled.div`
  overflow-x: auto;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const ChartWrapper = styled.div`
  min-width: 100%;
  display: flex;
`

const LabelsColumn = styled.div`
    width: 240px;
    min-width: 240px;
    max-width: 240px;
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.secondary};
  position: sticky;
  left: 0;
  z-index: 10;
`

const LabelHeader = styled.div`
  height: 50px;
  padding: 0 ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  font-weight: 600;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.secondary};
`

const LabelRow = styled.div`
  height: 40px;
  padding: 0 ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;

  &:hover {
    background: #e8eff2;
  }
`

const LabelText = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const ChartArea = styled.div`
  flex: 1;
  min-width: 600px;
  overflow: hidden;
`

const ChartHeader = styled.div`
  height: 50px;
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.secondary};
`

interface MonthCellProps {
    colWidth: number
}

const MonthCell = styled.div<MonthCellProps>`
  flex: 1;
  min-width: ${({ colWidth }) => colWidth}px;
  text-align: center;
  padding: 0.5rem 0.25rem;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  border-right: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-right: none;
  }
`

const RowsContainer = styled.div`
  position: relative;
`

interface TimelineRowProps {
    colWidth: number
}

const TimelineRow = styled.div<TimelineRowProps>`
  height: 40px;
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;

  & > div {
    flex: 1;
    min-width: ${({ colWidth }) => colWidth}px;
    border-right: 1px solid #f0f0f0;
  }

  & > div:last-child {
    border-right: none;
  }
`

const TodayLine = styled.div<{ left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ left }) => left}%;
  width: 2px;
  background: #F44336;
  z-index: 5;
`

const TodayLabel = styled.div`
  position: absolute;
  top: -20px;
  transform: translateX(-50%);
  background: #F44336;
  color: white;
  font-size: 0.65rem;
  padding: 2px 6px;
  border-radius: 4px;
`

interface BarProps {
    left: number
    width: number
    color: string
    top: number
    opacity?: number
}

const Bar = styled.div<BarProps>`
  position: absolute;
  height: 14px;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}%;
  width: ${({ width }) => width}%;
  background: ${({ color }) => color};
  opacity: ${({ opacity = 1 }) => opacity};
  border-radius: 4px;
  cursor: default;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  align-items: center;
  padding: 0 4px;
  font-size: 0.6rem;
  color: white;
  overflow: hidden;
  white-space: nowrap;

  &:hover {
    transform: scaleY(1.3);
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    z-index: 20;
  }
`

const EmptyMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
`

// Simple colors for Oportunidades view - blue for oferta, green for proyecto
const getProjectColor = (_project: any, type: 'oferta' | 'proyecto'): string => {
    return type === 'oferta' ? '#2196F3' : '#4CAF50'
}

// Fixed opacity for Oportunidades view
const getProjectOpacity = (_project: any): number => {
    return 0.85
}

const toDate = (value: any): Date | null => (value ? new Date(value) : null)

// The dates a phase bar spans; end null = open-ended. A phase with a start and
// no end runs to the right edge, the shared rule (load_rules.window_overlaps)
// that /timeline/projects lists by: such a row used to be listed, or dropped,
// with no bar at all. The offer bar is only open-ended while there is no
// project phase: once the project has dates the offer is over, and a missing
// delivery date must not paint it across the whole chart.
export const phaseDates = (p: any, phase: 'oferta' | 'proyecto'): { start: Date | null; end: Date | null } => {
    // The backend marks a start-only row open-ended only while its phase is live;
    // a closed one (lost offer, finished project) keeps a single-day reading.
    const closed = p.open_ended === false
    if (phase === 'proyecto') {
        const start = toDate(p.proyecto_fecha_inicio)
        const end = toDate(p.proyecto_fecha_fin)
        return { start, end: end ?? (closed ? start : null) }
    }
    const start = toDate(p.oferta_fecha_inicio)
    const end = toDate(p.oferta_fecha_entrega)
    const hasProjectPhase = Boolean(p.proyecto_fecha_inicio || p.proyecto_fecha_fin)
    if (!end && hasProjectPhase) return { start: null, end: null }
    return { start, end: end ?? (closed ? start : null) }
}

// Bar geometry in % of the range. No start = no bar; no end = to the right edge.
export const barPosition = (start: Date | null, end: Date | null, dateFrom: string, dateTo: string) => {
    if (!start) return null

    const from = new Date(dateFrom)
    const to = new Date(dateTo)
    const totalDays = (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
    const effectiveEnd = end ?? to

    if (start > to || effectiveEnd < from) return null

    const barStart = Math.max(0, (start.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
    const barEnd = Math.min(totalDays, (effectiveEnd.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
    const barDuration = barEnd - barStart

    if (barDuration <= 0) return null

    return {
        left: (barStart / totalDays) * 100,
        width: (barDuration / totalDays) * 100,
        openEnded: end === null,
    }
}

interface TimelineChartProps {
    data: any
    dateFrom: string
    dateTo: string
    granularity: string
    onOpportunityClick?: (project: any) => void
    onOpportunityNameClick?: (project: any) => void
}

export const TimelineChart: React.FC<TimelineChartProps> = ({
    data,
    dateFrom,
    dateTo,
    granularity,
    onOpportunityClick,
    onOpportunityNameClick,
}) => {
    const navigate = useNavigate()
    // Fallback when no click handler is passed: follow the shared contract
    // (admins open the editor in /oportunidades, others get nothing rather than
    // a bounce off the admin-only route). The chart can render without an
    // AuthProvider in tests; treat that as a non-admin.
    let isAdmin = false
    try {
        isAdmin = isAdminRole(useAuth().user?.role)
    } catch {
        isAdmin = false
    }

    const colWidth = useMemo(() => {
        switch (granularity) {
            case 'days': return 40
            case 'weeks': return 80
            default: return 100
        }
    }, [granularity])

    const periods = useMemo(() => {
        const result: { start: Date; label: string }[] = []
        const from = new Date(dateFrom)
        const to = new Date(dateTo)
        const current = new Date(from)

        switch (granularity) {
            case 'days':
                while (current <= to) {
                    result.push({
                        start: new Date(current),
                        label: current.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
                    })
                    current.setDate(current.getDate() + 1)
                }
                break
            case 'weeks':
                const startOfWeek = new Date(current)
                startOfWeek.setDate(current.getDate() - current.getDay() + 1)
                while (startOfWeek <= to) {
                    result.push({
                        start: new Date(startOfWeek),
                        label: startOfWeek.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
                    })
                    startOfWeek.setDate(startOfWeek.getDate() + 7)
                }
                break
            default:
                current.setDate(1)
                while (current <= to) {
                    result.push({
                        start: new Date(current),
                        label: current.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }),
                    })
                    current.setMonth(current.getMonth() + 1)
                }
        }

        return result
    }, [dateFrom, dateTo, granularity])

    const todayPosition = useMemo(() => {
        const today = new Date()
        const from = new Date(dateFrom)
        const to = new Date(dateTo)

        if (today < from || today > to) return null

        const totalDays = (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
        const dayOffset = (today.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
        return (dayOffset / totalDays) * 100
    }, [dateFrom, dateTo])

    const calculateBarPosition = (dates: { start: Date | null; end: Date | null }) =>
        barPosition(dates.start, dates.end, dateFrom, dateTo)

    const projects = data?.projects || []

    if (projects.length === 0) {
        return (
            <ChartContainer>
                <EmptyMessage>No hay oportunidades en el período seleccionado</EmptyMessage>
            </ChartContainer>
        )
    }

    return (
        <ChartContainer>
            <ChartWrapper>
                <LabelsColumn>
                    <LabelHeader>Oportunidad</LabelHeader>
                    {projects.map((p: any) => (
                        <LabelRow
                            key={p.id}
                            title={`${p.name} - Clic para ver equipo asignado`}
                            onClick={() => onOpportunityNameClick ? onOpportunityNameClick(p) : openOpportunity(navigate, p.id, { isAdmin })}
                        >
                            <LabelText>{p.name}</LabelText>
                        </LabelRow>
                    ))}
                </LabelsColumn>

                <ChartArea>
                    <ChartHeader>
                        {periods.map((m, i) => (
                            <MonthCell key={i} colWidth={colWidth}>{m.label}</MonthCell>
                        ))}
                    </ChartHeader>

                    <RowsContainer>
                        {todayPosition !== null && (
                            <TodayLine left={todayPosition}>
                                <TodayLabel>Hoy</TodayLabel>
                            </TodayLine>
                        )}

                        {projects.map((p: any) => {
                            const ofertaPos = calculateBarPosition(phaseDates(p, 'oferta'))
                            const proyectoPos = calculateBarPosition(phaseDates(p, 'proyecto'))
                            const openSuffix = ' (sin fecha de fin)'

                            const projectOpacity = getProjectOpacity(p)
                            return (
                                <TimelineRow key={p.id} colWidth={colWidth}>
                                    {periods.map((_, i) => <div key={i} />)}
                                    {ofertaPos && (
                                        <Bar
                                            left={ofertaPos.left}
                                            width={ofertaPos.width}
                                            color={getProjectColor(p, 'oferta')}
                                            opacity={projectOpacity}
                                            top={4}
                                            title={`Oferta: ${p.name}${ofertaPos.openEnded ? openSuffix : ''}`}
                                        >
                                            📋
                                        </Bar>
                                    )}
                                    {proyectoPos && (
                                        <Bar
                                            left={proyectoPos.left}
                                            width={proyectoPos.width}
                                            color={getProjectColor(p, 'proyecto')}
                                            opacity={projectOpacity}
                                            top={20}
                                            title={`Proyecto: ${p.name}${proyectoPos.openEnded ? openSuffix : ''}`}
                                        >
                                            🚀
                                        </Bar>
                                    )}
                                </TimelineRow>
                            )
                        })}
                    </RowsContainer>
                </ChartArea>
            </ChartWrapper>
        </ChartContainer>
    )
}
